import type { MeResponse } from '@/lib/api';
import { Icon, type IconName } from '@/components/Icon';
import { daysSince, daysWord, formatMemberSince, truncateLink } from '@/lib/format';
import { haptic, openExternalLink, openTelegramLink } from '@/lib/telegram';

interface ProfileScreenProps {
  me: MeResponse;
  // Routes the user into the setup flow's "other device" sub-screen (the
  // 2×2 platform picker). Owned by App so we don't need a parallel router
  // in here.
  onInstallOtherDevice: () => void;
  onCopy: () => void;
}

export function ProfileScreen({ me, onInstallOtherDevice, onCopy }: ProfileScreenProps) {
  const { user, subscription, links } = me;
  const firstName = (user.first_name || 'друг').toLowerCase();
  const initial = firstName.slice(0, 1) || 'i';
  const memberSince = formatMemberSince(user.registered_at);
  const days = daysSince(user.registered_at);
  const hasSubLink = Boolean(subscription.url) && subscription.state !== 'expired' && subscription.state !== 'new';
  const displayUrl = subscription.url;

  const handleCopy = async () => {
    if (!displayUrl) return;
    haptic('medium');
    let copied = false;
    try {
      await navigator.clipboard.writeText(displayUrl);
      copied = true;
    } catch {
      // Older Telegram in-app browsers block the Clipboard API; fall back to
      // a hidden textarea so the action still succeeds.
      const ta = document.createElement('textarea');
      ta.value = displayUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { copied = document.execCommand('copy'); } catch { /* both failed */ }
      document.body.removeChild(ta);
    }
    if (copied) onCopy();
  };

  return (
    <div
      className="no-scrollbar"
      style={{ padding: '8px 18px 130px', overflowY: 'auto', height: '100%' }}
    >
      {/* identity */}
      <div
        style={{
          marginTop: 8,
          marginBottom: 22,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 999,
            background: 'linear-gradient(135deg, #d4c5a8, #b8a280)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Instrument Serif', serif",
            fontSize: 30,
            color: 'var(--ink)',
            border: '1px solid var(--paper-3)',
            textTransform: 'lowercase',
          }}
        >
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 24,
              color: 'var(--ink)',
              letterSpacing: '-0.01em',
            }}
          >
            {firstName}
          </div>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: 11,
              color: 'var(--ink-3)',
              marginTop: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            id {user.telegram_id}
            {memberSince ? ` · ${memberSince}` : ''}
          </div>
        </div>
      </div>

      {/* "ты с нами уже X дней" — single stat (we don't track traffic). */}
      {days > 0 && (
        <div
          style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            borderRadius: 18,
            padding: 18,
            marginBottom: 18,
          }}
        >
          <div className="eyebrow" style={{ color: 'rgba(244,240,232,0.55)', marginBottom: 12 }}>
            ты с нами уже
          </div>
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: 38,
              lineHeight: 1,
              color: 'var(--paper)',
              letterSpacing: '-0.02em',
            }}
          >
            {days}
            <span style={{ fontSize: 16, color: 'rgba(244,240,232,0.55)', marginLeft: 8 }}>
              {daysWord(days)}
            </span>
          </div>
        </div>
      )}

      <Section title="поддержка">
        <Row
          icon="devices"
          title="установка на другом устройстве"
          subtitle="подробная инструкция"
          onClick={() => {
            haptic('light');
            onInstallOtherDevice();
          }}
        />
        <Row
          icon="msg"
          title="связаться с поддержкой"
          subtitle="обычно отвечаем минут за 10"
          onClick={() => {
            haptic('light');
            openTelegramLink(links.support_bot_url);
          }}
          last={!links.terms_url}
        />
        {links.terms_url && (
          <Row
            icon="receipt"
            title="пользовательское соглашение"
            subtitle="правила и политика"
            onClick={() => {
              haptic('light');
              openExternalLink(links.terms_url);
            }}
            last
          />
        )}
      </Section>

      {hasSubLink && (
        <div
          style={{
            marginTop: 22,
            background: '#fbf8f1',
            border: '1px solid var(--paper-3)',
            borderRadius: 16,
            padding: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>
              твоя ссылка на подписку
            </div>
            <div
              style={{
                fontFamily: 'var(--f-mono)',
                fontSize: 12,
                color: 'var(--ink-2)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {truncateLink(displayUrl)}
            </div>
          </div>
          <button
            onClick={() => void handleCopy()}
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: 'var(--ink)',
              color: 'var(--paper)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-label="скопировать ссылку"
          >
            <Icon name="copy" size={15} stroke="currentColor" sw={1.8} />
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="eyebrow" style={{ marginBottom: 10, paddingLeft: 4 }}>
        {title}
      </div>
      <div
        style={{
          background: '#fbf8f1',
          border: '1px solid var(--paper-3)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
}

interface RowProps {
  icon: IconName;
  title: string;
  subtitle: string;
  onClick: () => void;
  last?: boolean;
}

function Row({ icon, title, subtitle, onClick, last }: RowProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        // Order matters: the `border` shorthand must come first so the
        // `borderBottom` longhand isn't reset by it (React applies inline
        // styles in object-key order). Without this the row dividers in
        // the support section render as zero-width.
        border: 'none',
        borderBottom: last ? 'none' : '1px solid var(--paper-3)',
        cursor: 'pointer',
        background: 'transparent',
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: 'var(--paper-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={16} stroke="var(--ink)" sw={1.6} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{subtitle}</div>
      </div>
      <Icon name="chev" size={14} stroke="var(--ink-4)" sw={1.8} />
    </button>
  );
}
